// Operator › Connections › Ops automation account (OPS-AUTH, 2026-10-29): the service account
// maintenance scripts act as — never a person's password or session. A platform super admin issues
// it (an agency operator with no password, on every company the agency operates) and revokes it
// (its access, agency row and open sessions). Both are audited (`cp.platform_audit`).
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Notice,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { problemText } from "../ops-parts";

const opsKey = ["operator", "ops-account"] as const;
const opsQuery = (api: Api) =>
  queryOptions({ queryKey: opsKey, queryFn: () => api.client.operator.opsAccount.get() });

export function OpsAccountCard({ api }: { api: Api }) {
  const qc = useQueryClient();
  const view = useQuery(opsQuery(api));
  const [revoking, setRevoking] = useState(false);
  const done = () => void qc.invalidateQueries({ queryKey: opsKey });
  const issue = useMutation({
    mutationFn: () => api.client.operator.opsAccount.issue({ body: {} }),
    onSuccess: done,
  });
  const revoke = useMutation({
    mutationFn: () => api.client.operator.opsAccount.revoke({ body: {} }),
    onSuccess: () => {
      setRevoking(false);
      done();
    },
  });
  const v = view.data;
  return (
    <section
      className="rounded-lg border border-border bg-card p-4"
      data-slot="ops-account"
      aria-label="Ops automation account"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            Ops automation account{" "}
            {v ? (
              v.issued ? (
                <Badge variant="accent">Issued</Badge>
              ) : (
                <Badge variant="outline">Not issued</Badge>
              )
            ) : null}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            What maintenance scripts sign in as. A service account with no password, never a person.
          </p>
        </div>
        {v ? (
          <div className="flex shrink-0 flex-wrap items-center gap-1">
            <Button
              variant="secondary"
              size="xs"
              disabled={issue.isPending}
              onClick={() => issue.mutate()}
            >
              {v.issued ? "Re-issue" : "Issue"}
            </Button>
            {v.issued ? (
              <Button variant="ghost" size="xs" onClick={() => setRevoking(true)}>
                Revoke
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {v?.issued ? (
        <dl className="mt-3 grid gap-1 text-xs sm:grid-cols-[10rem_1fr]">
          <dt className="text-muted-foreground">Address</dt>
          <dd className="min-w-0 break-all">
            <code>{v.email}</code>
          </dd>
          {v.issued_at ? (
            <>
              <dt className="text-muted-foreground">Issued</dt>
              <dd>{formatDateTime(v.issued_at)}</dd>
            </>
          ) : null}
          <dt className="text-muted-foreground">Companies</dt>
          <dd>{v.companies.length ? v.companies.map((c) => c.name).join(", ") : "None"}</dd>
          <dt className="text-muted-foreground">Open sessions</dt>
          <dd>{v.open_sessions}</dd>
        </dl>
      ) : null}
      {view.error ? (
        <Notice tone="danger" className="mt-3">
          {problemText(view.error, "The ops account could not be read.")}
        </Notice>
      ) : null}
      {issue.error ? (
        <Notice tone="danger" className="mt-3">
          {problemText(issue.error, "The ops account could not be issued.")}
        </Notice>
      ) : null}
      <Dialog open={revoking} onOpenChange={setRevoking}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke the ops automation account?</DialogTitle>
            <DialogDescription>
              Scripts stop working until it is issued again. Its open sessions end now.
            </DialogDescription>
          </DialogHeader>
          {revoke.error ? (
            <Notice tone="danger">{problemText(revoke.error, "Not revoked.")}</Notice>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button
              variant="destructive"
              disabled={revoke.isPending}
              onClick={() => revoke.mutate()}
            >
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
